import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

interface GetMatchesParams {
  active_only: boolean;
  on_date?: string;
}

// Define and export the Player type directly
export type Player = {
  id: number;
  name: string;
  rating_mu: number;
  rating_rd: number;
  rating_vol: number;
  status: 'on_court' | 'available' | 'unavailable' | 'absent';
  matches_played: number;
  consecutive_waits: number;
};

// Define the Court type
export type Court = {
  id: number;
  name: string;
};

// Define the Match type
export type Match = {
  id: number;
  court_id: number;
  start_time: string;
  end_time: string | null;
  players: Player[];
  court: Court;
  team_a: number[];
  team_b: number[];
};


// Create an axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Player API Functions ---

export const getPlayers = async (): Promise<Player[]> => {
  try {
    const response = await apiClient.get('/players/');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching players:", error);
    throw error;
  }
};

export const updatePlayerStatus = async (id: number, status: 'on_court' | 'available' | 'resting' | 'unavailable'): Promise<Player> => {
  const response = await apiClient.put(`/players/${id}`, { status });
  return response.data;
};

export type PlayerCreate = {
  name: string;
  rating_mu?: number;
  rating_rd?: number;
  rating_vol?: number;
};

export type PlayerUpdate = {
  name?: string;
  rating_mu?: number;
  rating_rd?: number;
  rating_vol?: number;
  status?: 'on_court' | 'available' | 'unavailable' | 'absent';
};

export const createPlayer = async (player: PlayerCreate): Promise<Player> => {
  const response = await apiClient.post('/players/', player);
  return response.data;
};

export const updatePlayer = async (id: number, player: PlayerUpdate): Promise<Player> => {
  const response = await apiClient.put(`/players/${id}`, player);
  return response.data;
};

export const deletePlayer = async (id: number): Promise<Player> => {
  const response = await apiClient.delete(`/players/${id}`);
  return response.data;
};

// --- Court API Functions ---

export const getActiveCourtCount = async (): Promise<number> => {
    const response = await apiClient.get('/courts/active_count');
    return response.data;
}

export type CourtCreate = {
  name: string;
};

export type CourtUpdate = {
  name?: string;
};

export const getCourts = async (): Promise<Court[]> => {
  try {
    const response = await apiClient.get('/courts/');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching courts:", error);
    throw error;
  }
};

export const createCourt = async (court: CourtCreate): Promise<Court> => {
  const response = await apiClient.post('/courts/', court);
  return response.data;
};

export const updateCourt = async (id: number, court: CourtUpdate): Promise<Court> => {
  const response = await apiClient.put(`/courts/${id}`, court);
  return response.data;
};

export const deleteCourt = async (id: number): Promise<Court> => {
  const response = await apiClient.delete(`/courts/${id}`);
  return response.data;
};


// --- Scheduler API Functions ---

export const generateMatchForCourt = async (courtId: number): Promise<Match> => {
    const response = await apiClient.post(`/scheduler/generate_match/${courtId}`);
    return response.data;
}

export const regenerateMatch = async (courtId: number): Promise<Match> => {
  const response = await apiClient.post(`/scheduler/regenerate_match/${courtId}`);
  return response.data;
};

// --- Match API Functions ---

export type MatchCreate = {
  court_id: number;
  player_ids: number[];
  teams: { team1: number[], team2: number[] };
  quality_score: number;
};

export const createMatch = async (match: MatchCreate): Promise<Match> => {
    const response = await apiClient.post('/matches/', match);
    return response.data;
};

export const endMatch = async (matchId: number): Promise<Match> => {
    const response = await apiClient.post(`/matches/${matchId}/end`);
    return response.data;
}

export const deleteMatches = async (matchIds: number[]): Promise<void> => {
    await apiClient.post('/matches/bulk_delete', { match_ids: matchIds });
    // Assuming backend returns success status and content is not strictly used
}

export const getMatches = async (activeOnly: boolean = false, onDate: string | null = null): Promise<Match[]> => {
  try {
    const params: GetMatchesParams = { active_only: activeOnly };
    if (onDate) {
      params.on_date = onDate;
    }
    const response = await apiClient.get('/matches/', { params });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching matches:", error);
    throw error;
  }
};

// --- History API Functions ---

export type MatrixData = {
    [key: string]: { [key: string]: number }
};

export type HistoryMatrices = {
    partner_matrix: MatrixData;
    opponent_matrix: MatrixData;
};

export const getHistoryMatrices = async (): Promise<HistoryMatrices> => {
    try {
        const response = await apiClient.get('/history/matrices');
        return response.data;
    } catch (error) {
        console.error("Error fetching history matrices:", error);
        throw error;
    }
}

// --- Settings API Functions ---

export type Setting = {
  key: string;
  value: string;
};

export const getSettings = async (): Promise<Setting[]> => {
  const response = await apiClient.get('/settings/');
  return response.data;
};

export const updateSettings = async (settings: Setting[]): Promise<Setting[]> => {
  const response = await apiClient.post('/settings/', settings);
  return response.data;
};

export const refillBuffer = async (): Promise<{ message: string }> => {
  const response = await apiClient.post('/scheduler/refill_buffer');
  return response.data;
};

export const getBuffer = async (): Promise<any[]> => {
  const response = await apiClient.get('/scheduler/buffer');
  return response.data;
};

// --- Health Check API Functions ---

export const pingBackend = async (): Promise<{ status: string }> => {
  // We use a different axios instance for the health check to avoid the '/api' prefix
  const healthCheckApiClient = axios.create({
    baseURL: API_BASE_URL.replace('/api', ''),
  });
  const response = await healthCheckApiClient.get('/');
  return response.data;
};

